/**
 * Hook: POST /admin/super-admins/invite — invite another super-admin (RBAC FE WS-D).
 *
 * SA-host only; the trigger affordance is hidden under impersonation (#8), but
 * the backend `require_not_impersonating` on the route is the real boundary
 * (`app/api/admin/sa_users.py:80-107`). The invited SA is created immediately
 * with a temporary password + `requires_password_reset=TRUE`; they sign in on
 * the admin host and the WS-E temp-password interceptor redirects them to
 * `/admin/change-password` at first login. The success copy reflects that
 * honestly (temp password emailed, must be changed at first login) rather than
 * promising instant access.
 *
 * Distinct backend error code (surfaced by the console, never collapsed):
 *   sa_already_exists  409  — a super-admin already exists for that email.
 *
 * On success the caller invalidates the SA-users list so the new SA surfaces
 * without a manual refresh (#7).
 */
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { ApiError } from "@/lib/apiError";
import { saUsersKey } from "./saUserKeys";

interface InviteSuperAdminVars {
  email: string;
}

interface InviteSuperAdminResult {
  user_id: string;
  invite_id: string;
  email: string;
  expires_at_utc: string;
}

export function useInviteSuperAdmin(): UseMutationResult<
  InviteSuperAdminResult,
  ApiError,
  InviteSuperAdminVars
> {
  const qc = useQueryClient();
  return useMutation<InviteSuperAdminResult, ApiError, InviteSuperAdminVars>({
    mutationFn: async ({ email }) => {
      const resp = await apiClient.post<InviteSuperAdminResult>(
        "/admin/super-admins/invite",
        { email },
      );
      return resp.data;
    },
    onSuccess: () => {
      // #7 — re-sync the list with the backend after the write.
      qc.invalidateQueries({ queryKey: saUsersKey() });
    },
  });
}
